import { Box, DialogActions, DialogContent, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Domain, Hooks } from '@3nickels/data-modules';
import { BootstrapDialog } from '../../../../components/Modal';
import { Svgs } from '../../../../assets/svg';
import { useFindYourInstitutionModal } from '../../../../hooks/useFindYourInstitutionModal';
import { useModalContentView } from '../../../layout/ContentViewContext';
import { Colors } from '../../../../themes';
import { FindInstitutionStep, ContinueLinkingItem, ManualMenuOption } from './Types';
import {
  deferUntilAfterPaint,
  getDebtMenuItems,
  getInvestmentsMenuItems,
  getManualCategoryCards,
  getOtherMenuItems,
  getPropertiesMenuItems,
  getRetirementMenuItems,
} from './utils';
import { AppleStep, BrowseStep, ManualStep, OwnerStep } from './FindYourInstitutionModalSteps';
import ContinueLinkingLoader from './ContinueLinkingLoader';
import PlaidEmbeddedInstitutionSearch from './PlaidEmbeddedInstitutionSearch';
import { Button } from '../../../../components/buttons/Button';

const FindYourInstitutionModal: React.FC = () => {
  const { isModalOpen } = useModalContentView();
  const { modalKey } = useFindYourInstitutionModal();

  if (!isModalOpen(modalKey)) {
    return null;
  }

  return <FindYourInstitutionModalContent />;
};

const FindYourInstitutionModalContent: React.FC = () => {
  const { t } = useTranslation();
  const { closeFindYourInstitutionModal, modalOptions } = useFindYourInstitutionModal();
  const wizard = Hooks.usePlaidWizard();
  const plaidContext = Hooks.usePlaidContext();
  const { includeSpouse } = Hooks.useCombinedSelfData();
  const existingAccountParticipantId =
    (plaidContext.account as (Domain.ILinkableAccount & Domain.OwnedAccount) | undefined)
      ?.participantId ?? 0;
  const isExistingAccountSpouse = Hooks.useIsSpouseQuery(existingAccountParticipantId);
  const existingAccountOwner: Domain.Owner = isExistingAccountSpouse ? 'spouse' : 'me';
  const shouldShowContinueLinking = !modalOptions?.hideContinueLinkingSection;
  const shouldShowManualAddButton = !modalOptions?.hideManualAddButton;

  const [step, setStep] = useState<FindInstitutionStep>('browse');
  const [selectedOwner, setSelectedOwner] = useState<Domain.Owner>('me');
  const [continueLinkingItems, setContinueLinkingItems] = useState<ContinueLinkingItem[]>([]);

  const [shouldLoadContinueLinking, setShouldLoadContinueLinking] = useState(false);
  const [continueLinkingLoadVersion, setContinueLinkingLoadVersion] = useState(0);
  const [embeddedPlaidSearchVersion, setEmbeddedPlaidSearchVersion] = useState(0);
  const [manualMenuAnchorEl, setManualMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [manualMenuItems, setManualMenuItems] = useState<ManualMenuOption[]>([]);
  const [pendingPublicToken, setPendingPublicToken] = useState<string | null>(null);

  const scheduleContinueLinkingReload = useCallback(() => {
    setContinueLinkingItems([]);
    setShouldLoadContinueLinking(false);
    if (!shouldShowContinueLinking) {
      return;
    }

    deferUntilAfterPaint(() => {
      setContinueLinkingLoadVersion((current) => current + 1);
      setShouldLoadContinueLinking(true);
    });
  }, [shouldShowContinueLinking]);

  const handlePlaidExit = useCallback(() => {
    setPendingPublicToken(null);
    setSelectedOwner('me');
    setStep('browse');
    scheduleContinueLinkingReload();
    setEmbeddedPlaidSearchVersion((current) => current + 1);
    void wizard.generateLinkToken();
  }, [scheduleContinueLinkingReload, wizard]);

  const handleClose = useCallback(() => {
    setPendingPublicToken(null);
    setSelectedOwner('me');
    setStep('browse');
    closeFindYourInstitutionModal();

    deferUntilAfterPaint(() => {
      wizard.chooseOwner('me');
    });
  }, [closeFindYourInstitutionModal, wizard]);

  const closeManualMenu = useCallback(() => {
    setManualMenuAnchorEl(null);
    setManualMenuItems([]);
  }, []);

  const openManualMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, items: ManualMenuOption[]) => {
      setManualMenuAnchorEl(event.currentTarget);
      setManualMenuItems(items);
    },
    []
  );

  const retirementMenuItems = useMemo<ManualMenuOption[]>(() => getRetirementMenuItems(t), [t]);

  const propertiesMenuItems = useMemo<ManualMenuOption[]>(() => getPropertiesMenuItems(t), [t]);

  const investmentsMenuItems = useMemo<ManualMenuOption[]>(() => getInvestmentsMenuItems(t), [t]);

  const debtMenuItems = useMemo<ManualMenuOption[]>(() => getDebtMenuItems(t), [t]);

  const otherMenuItems = useMemo<ManualMenuOption[]>(() => getOtherMenuItems(t), [t]);

  const manualCategoryCards = useMemo(
    () =>
      getManualCategoryCards({
        debtMenuItems,
        handleClose,
        investmentsMenuItems,
        onManualNavigation: modalOptions?.onManualNavigation,
        otherMenuItems,
        propertiesMenuItems,
        retirementMenuItems,
        t,
      }),
    [
      debtMenuItems,
      handleClose,
      investmentsMenuItems,
      modalOptions,
      otherMenuItems,
      propertiesMenuItems,
      retirementMenuItems,
      t,
    ]
  );

  useEffect(() => {
    scheduleContinueLinkingReload();
  }, [scheduleContinueLinkingReload]);

  const handleManualMenuItemClick = useCallback(
    async (item: ManualMenuOption) => {
      closeManualMenu();
      handleClose();
      await modalOptions?.onManualNavigation?.(item.target, item.subtype);
    },
    [closeManualMenu, handleClose, modalOptions]
  );

  const handleContinueLinkingClick = useCallback(
    async (item: ContinueLinkingItem) => {
      handleClose();
      await modalOptions?.onContinueLinking?.(item.accessId);
    },
    [handleClose, modalOptions]
  );

  useEffect(() => {
    wizard.chooseOwner(selectedOwner);
  }, [selectedOwner, wizard]);

  // Generate a fresh link token on every modal open. The component unmounts
  // when the modal closes, so this fires on each new open — preventing stale
  // tokens (wrong product, already-consumed) from being reused across sessions.
  useEffect(() => {
    void wizard.generateLinkToken();
  }, []); // mount-only: fresh token per open

  const handleOwnerChange = useCallback((owner: string) => {
    setSelectedOwner(owner as Domain.Owner);
  }, []);

  const completeAutoLinkFlow = useCallback(
    async (publicToken: string, owner: Domain.Owner) => {
      wizard.chooseOwner(owner);
      if (plaidContext.account) {
        await wizard.startManualLinkJob(
          publicToken,
          plaidContext.filter?.filter ?? Domain.FinancialAccountTypeEnum.InvestmentAccount
        );
      } else {
        await wizard.startAutoLinkJob(publicToken);
      }
      handleClose();
      await modalOptions?.onAutoLinkSuccess?.();
    },
    [handleClose, modalOptions, plaidContext.account, plaidContext.filter?.filter, wizard]
  );

  const handleEmbeddedPlaidSuccess = useCallback(
    async (publicToken: string) => {
      if (plaidContext.account) {
        // Account already has a determined owner (came from summary screen); skip owner step
        await completeAutoLinkFlow(publicToken, existingAccountOwner);
        return;
      }

      if (includeSpouse) {
        setPendingPublicToken(publicToken);
        setStep('owner');
        return;
      }

      await completeAutoLinkFlow(publicToken, 'me');
    },
    [completeAutoLinkFlow, existingAccountOwner, includeSpouse, plaidContext.account]
  );

  const handleOwnerChoose = useCallback(async () => {
    if (!pendingPublicToken) {
      return;
    }

    await completeAutoLinkFlow(pendingPublicToken, selectedOwner);
  }, [completeAutoLinkFlow, pendingPublicToken, selectedOwner]);

  const embeddedInstitutionSearch = useMemo(
    () => (
      <PlaidEmbeddedInstitutionSearch
        key={embeddedPlaidSearchVersion}
        onExit={handlePlaidExit}
        onSuccess={handleEmbeddedPlaidSuccess}
        plaidContextToken={plaidContext.token ?? null}
        wizard={wizard}
      />
    ),
    [
      embeddedPlaidSearchVersion,
      handleEmbeddedPlaidSuccess,
      handlePlaidExit,
      modalOptions,
      plaidContext.token,
      wizard,
    ]
  );

  return (
    <>
      {shouldShowContinueLinking && shouldLoadContinueLinking ? (
        <ContinueLinkingLoader
          key={continueLinkingLoadVersion}
          onItemsChange={setContinueLinkingItems}
          wizard={wizard}
        />
      ) : null}
      <BootstrapDialog
        fullWidth
        maxWidth={false}
        onClose={handleClose}
        aria-labelledby='find-your-institution-title'
        open
        sx={{
          '& .MuiDialogActions-root': {
            boxSizing: 'border-box',
            marginBottom: 0,
            marginLeft: 0,
            marginRight: 0,
            padding: '0 !important',
            width: '100%',
          },
          '& .MuiDialogContent-root': {
            boxSizing: 'border-box',
            margin: 0,
            padding: '0 !important',
            width: '100%',
          },
          '& .MuiIconButton-root': {
            height: 'auto',
            margin: 0,
            minHeight: 0,
            minWidth: 0,
            padding: 0,
            position: 'static',
            right: 'auto',
            top: 'auto',
            transform: 'none',
            width: 'auto',
          },
          '& .MuiIconButton-root .MuiTouchRipple-root': {
            display: 'none',
          },
          '& .MuiPaper-root': {
            margin: { xs: 1.5, sm: 3 },
            maxWidth: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 48px)' },
            overflow: step === 'manual' ? 'hidden' : 'visible',
            padding: step === 'manual' ? '18px 14px 18px 14px' : '16px 16px 20px 16px',
            width: {
              xs: 'calc(100vw - 24px)',
              sm: 'calc(100vw - 48px)',
              md: step === 'manual' ? 690 : 880,
            },
          },
        }}>
        <DialogContent
          sx={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            p: 0,
          }}>
          {step === 'browse' ? (
            <BrowseStep
              continueLinkingItems={continueLinkingItems}
              embeddedInstitutionSearch={embeddedInstitutionSearch}
              hideContinueLinkingSection={!shouldShowContinueLinking}
              onAppleClick={() => setStep('apple')}
              onClose={handleClose}
              onContinueLinkingClick={handleContinueLinkingClick}
            />
          ) : null}
          {step === 'owner' ? (
            <OwnerStep
              onBack={() => {
                setPendingPublicToken(null);
                setSelectedOwner('me');
                setStep('browse');
              }}
              onClose={handleClose}
              onOwnerSelect={handleOwnerChange}
              selectedOwner={selectedOwner}
            />
          ) : null}
          {step === 'apple' ? (
            <AppleStep onBack={() => setStep('browse')} onClose={handleClose} />
          ) : null}
          {step === 'manual' ? (
            <ManualStep
              closeManualMenu={closeManualMenu}
              handleManualMenuItemClick={handleManualMenuItemClick}
              manualCategoryCards={manualCategoryCards}
              manualMenuAnchorEl={manualMenuAnchorEl}
              manualMenuItems={manualMenuItems}
              otherMenuItems={otherMenuItems}
              onBack={() => {
                closeManualMenu();
                setStep('browse');
              }}
              onClose={handleClose}
              openManualMenu={openManualMenu}
            />
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            alignItems: 'stretch',
            boxSizing: 'border-box',
            flexDirection: step === 'owner' ? 'row' : 'column',
            gap: 1.5,
            minWidth: 0,
            mt:
              step === 'browse' &&
              shouldShowManualAddButton &&
              !(shouldShowContinueLinking && continueLinkingItems.length > 0)
                ? '20px'
                : 0,
            padding: '0 !important',
            width: '100%',
          }}>
          {step === 'owner' ? (
            <>
              <Button
                color='primary'
                onClick={handleClose}
                sx={{ minWidth: 100, width: 100 }}
                variant='outlined'>
                {t('Cancel')}
              </Button>
              <Button
                color='primary'
                onClick={handleOwnerChoose}
                sx={{ minWidth: 100, width: 100 }}
                variant='contained'>
                {t('Choose')}
              </Button>
            </>
          ) : null}
          {step === 'browse' && shouldShowManualAddButton ? (
            <Button
              fullWidth
              onClick={() => setStep('manual')}
              sx={{
                'alignItems': 'center',
                'backgroundColor': Colors.secondaryLightest,
                'border': `1px solid ${Colors.primaryLightest}`,
                'borderRadius': '8px',
                'color': Colors.primaryBase,
                'display': 'flex',
                'justifyContent': 'space-between',
                'minWidth': 0,
                'overflow': 'hidden',
                'px': 1.25,
                'py': 0.875,
                'textTransform': 'none',
                '&:hover': {
                  backgroundColor: Colors.primaryLighter,
                },
              }}
              variant='text'>
              <Typography
                color='primary'
                noWrap
                sx={{ flex: 1, textAlign: 'left' }}
                variant='p14Bold'>
                {t('CantLinkAddManuallyInstead')}
              </Typography>
              <Box sx={{ display: 'flex', flexShrink: 0, ml: 0.75 }}>
                <Svgs.ActionSmallPlus />
              </Box>
            </Button>
          ) : null}
        </DialogActions>
      </BootstrapDialog>
    </>
  );
};

export default FindYourInstitutionModal;
